/**
 * Tutorial and Onboarding Service
 */

import type { OnboardingState, Tutorial, TutorialProgress, UserJourney } from '@/types/tutorial';

const TUTORIALS: Tutorial[] = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'Quick tour of navigation, help, glossary, and journeys',
    icon: '✨',
    estimatedTime: 3,
    difficulty: 'beginner',
    steps: [
      {
        id: 'nav-overview',
        title: 'Navigation Menu',
        description: 'Use the left sidebar to reach dashboards, CMDB, change requests, and security.',
        target: '.nav-dashboard',
        position: 'right',
        showArrow: true,
        skipable: true,
      },
      {
        id: 'help-center',
        title: 'Help & Guides',
        description: 'Open Help to explore walkthroughs, FAQs, and best practices.',
        target: '.nav-help',
        position: 'right',
        showArrow: true,
        skipable: true,
      },
      {
        id: 'glossary',
        title: 'Glossary',
        description: 'Find definitions for CMDB, CAB, PIR, vulnerabilities, and more.',
        target: '.nav-glossary',
        position: 'right',
        showArrow: true,
        skipable: true,
      },
      {
        id: 'journeys',
        title: 'User Journeys',
        description: 'Follow visual flows tailored to Project Managers, Change Managers, Security Analysts, and Developers.',
        target: '.nav-journey',
        position: 'right',
        showArrow: true,
        skipable: true,
      },
    ],
  },
  {
    id: 'cmdb-tour',
    title: 'CMDB Essentials',
    description: 'Add configuration items, set relationships, and view statuses.',
    icon: '🗂️',
    estimatedTime: 5,
    difficulty: 'beginner',
    steps: [
      {
        id: 'cmdb-nav',
        title: 'Open CMDB',
        description: 'Navigate to CMDB to view configuration items.',
        target: '.nav-cmdb',
        position: 'right',
        showArrow: true,
      },
      {
        id: 'cmdb-add',
        title: 'Add Configuration Item',
        description: 'Use the Add button to register a server, application, or database.',
        target: '.cmdb-add-button',
        position: 'bottom',
        showArrow: true,
      },
      {
        id: 'cmdb-rel',
        title: 'Set Relationships',
        description: 'Connect dependencies to map impact and risk.',
        target: '.cmdb-relationships-panel',
        position: 'left',
        showArrow: true,
      },
    ],
  },
  {
    id: 'change-requests',
    title: 'Change Management',
    description: 'Submit, approve, and track change requests with CAB/PIR gates.',
    icon: '🔄',
    estimatedTime: 6,
    difficulty: 'intermediate',
    steps: [
      {
        id: 'changes-nav',
        title: 'Change Requests',
        description: 'Open the Change Requests tab to see active changes and PIR status.',
        target: '.nav-cmdb',
        position: 'right',
        showArrow: true,
      },
      {
        id: 'changes-create',
        title: 'Create a Change',
        description: 'Click “New Change” to propose a deployment with risk and impact.',
        target: '.change-new-button',
        position: 'bottom',
        showArrow: true,
      },
      {
        id: 'pir-status',
        title: 'PIR & Overrides',
        description: 'Track post-implementation reviews and request CAB overrides when needed.',
        target: '.pir-status-block',
        position: 'left',
        showArrow: true,
      },
    ],
  },
];

const JOURNEYS: UserJourney[] = [];

const STORAGE_KEY = 'sdlc_onboarding_state';
const TUTORIAL_PROGRESS_KEY = 'sdlc_tutorial_progress';

class TutorialService {
  /**
   * Initialize first-time user onboarding state
   */
  initializeOnboarding(): OnboardingState {
    const existing = this.getOnboardingState();
    if (existing) return existing;

    const state: OnboardingState = {
      firstTimeUser: true,
      completedTutorials: [],
      completedJourneys: [],
      dismissedDots: [],
      lastOnboardingStepAt: new Date(),
      preferences: {
        showDots: true,
        autoplayTutorials: true,
        highlightMode: 'full',
      },
    };

    this.saveOnboardingState(state);
    return state;
  }

  /**
   * Get current onboarding state
   */
  getOnboardingState(): OnboardingState | null {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  }

  /**
   * Save onboarding state
   */
  saveOnboardingState(state: OnboardingState): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }

  /**
   * Mark tutorial as completed
   */
  completeTutorial(tutorialId: string): void {
    const state = this.getOnboardingState();
    if (state && !state.completedTutorials.includes(tutorialId)) {
      state.completedTutorials.push(tutorialId);
      state.lastOnboardingStepAt = new Date();
      this.saveOnboardingState(state);
    }
  }

  /**
   * Mark journey as completed
   */
  completeJourney(journeyId: string): void {
    const state = this.getOnboardingState();
    if (state && !state.completedJourneys.includes(journeyId)) {
      state.completedJourneys.push(journeyId);
      this.saveOnboardingState(state);
    }
  }

  /**
   * Dismiss a blue dot indicator
   */
  dismissBlueDot(dotId: string): void {
    const state = this.getOnboardingState();
    if (state && !state.dismissedDots.includes(dotId)) {
      state.dismissedDots.push(dotId);
      this.saveOnboardingState(state);
    }
  }

  /**
   * Check if blue dot should be shown
   */
  shouldShowBlueDot(dotId: string): boolean {
    const state = this.getOnboardingState();
    return state ? !state.dismissedDots.includes(dotId) && state.preferences?.showDots !== false : true;
  }

  /**
   * Get tutorial progress
   */
  getTutorialProgress(tutorialId: string): TutorialProgress | null {
    const stored = localStorage.getItem(`${TUTORIAL_PROGRESS_KEY}_${tutorialId}`);
    return stored ? JSON.parse(stored) : null;
  }

  /**
   * Save tutorial progress
   */
  saveTutorialProgress(progress: TutorialProgress): void {
    localStorage.setItem(`${TUTORIAL_PROGRESS_KEY}_${progress.tutorialId}`, JSON.stringify(progress));
  }

  /**
   * Check if tutorial is completed
   */
  isTutorialCompleted(tutorialId: string): boolean {
    const state = this.getOnboardingState();
    return state ? state.completedTutorials.includes(tutorialId) : false;
  }

  /**
   * Mark first-time user status
   */
  setFirstTimeUserComplete(): void {
    const state = this.getOnboardingState();
    if (state) {
      state.firstTimeUser = false;
      this.saveOnboardingState(state);
    }
  }

  /**
   * Reset all onboarding data
   */
  resetOnboarding(): void {
    localStorage.removeItem(STORAGE_KEY);
    // Clear all tutorial progress
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(TUTORIAL_PROGRESS_KEY)) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Get all available tutorials (to be implemented with actual data)
   */
  getAvailableTutorials(): Tutorial[] {
    return TUTORIALS;
  }

  /**
   * Get all available user journeys (to be implemented with actual data)
   */
  getAvailableJourneys(): UserJourney[] {
    return JOURNEYS;
  }

  /**
   * Update preferences
   */
  updatePreferences(preferences: Partial<OnboardingState['preferences']>): void {
    const state = this.getOnboardingState();
    if (state) {
      state.preferences = { ...state.preferences, ...preferences };
      this.saveOnboardingState(state);
    }
  }
}

const tutorialService = new TutorialService();

export default tutorialService;
